import { useState, useEffect, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api, adminAuthHeaders, adminToken, setAdminToken, clearAdminToken } from "../lib/api";
import { ArrowLeft, Trash, Plus, ArrowRight, EnvelopeSimple, Phone as PhoneIcon } from "@phosphor-icons/react";
import Seo from "../components/Seo";

const STATUSES = ["new", "contacted", "won", "lost"];

function Login({ onLogin }) {
  const [email, setEmail] = useState("admin@themelbournepainters.com.au");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      const r = await api.post("/admin/login", { email, password });
      setAdminToken(r.data.token);
      onLogin();
    } catch (e2) {
      setErr(e2?.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#1A1A1A] text-[#F2F2EF] flex items-center justify-center p-5 grain">
      <form
        onSubmit={submit}
        data-testid="admin-login-form"
        className="border-2 border-[#F2F2EF] p-8 md:p-10 w-full max-w-md bg-[#222]"
      >
        <Link to="/" className="text-xs uppercase tracking-widest text-[#999] hover:text-[#D85C3A] flex items-center gap-1">
          <ArrowLeft size={14} /> Back to site
        </Link>
        <h1 className="font-display text-4xl md:text-5xl uppercase tracking-tighter mt-3">Admin login</h1>
        <p className="text-[#999] text-sm mt-2">The Melbourne Painters · Internal dashboard</p>

        <div className="mt-8">
          <label className="text-xs uppercase tracking-widest text-[#999]">Email</label>
          <input data-testid="admin-email" type="email" autoComplete="email" className="input-brut text-[#F2F2EF]" style={{ borderBottomColor: "#F2F2EF" }} value={email} onChange={(e) => setEmail(e.target.value)} />
        </div>
        <div className="mt-5">
          <label className="text-xs uppercase tracking-widest text-[#999]">Password</label>
          <input data-testid="admin-password" type="password" className="input-brut text-[#F2F2EF]" style={{ borderBottomColor: "#F2F2EF" }} value={password} onChange={(e) => setPassword(e.target.value)} />
        </div>
        {err && <div className="mt-4 text-sm text-[#FFB4A2]">{err}</div>}
        <button data-testid="admin-login-submit" type="submit" disabled={loading} className="btn-brut mt-8 w-full justify-center">
          {loading ? "Logging in..." : "Log in"} <ArrowRight size={18} weight="bold" />
        </button>
      </form>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="border-2 border-[#111] p-5 bg-[#F2F2EF]">
      <div className="text-xs uppercase tracking-widest text-[#666]">{label}</div>
      <div className="font-display text-4xl md:text-5xl mt-1">{value}</div>
    </div>
  );
}

function LeadsTab() {
  const [leads, setLeads] = useState([]);
  const [filter, setFilter] = useState("all");
  const load = useCallback(async () => {
    const r = await api.get("/leads", { headers: adminAuthHeaders() });
    setLeads(r.data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const updateStatus = async (id, status) => {
    await api.patch(`/leads/${id}?status=${status}`, {}, { headers: adminAuthHeaders() });
    load();
  };
  const del = async (id) => {
    if (!confirm("Delete this lead?")) return;
    await api.delete(`/leads/${id}`, { headers: adminAuthHeaders() });
    load();
  };

  const shown = filter === "all" ? leads : leads.filter((l) => l.status === filter);

  return (
    <div>
      <div className="flex flex-wrap gap-2 mb-5">
        <button data-testid="leads-filter-all" onClick={() => setFilter("all")} className={`chip ${filter === "all" ? "active" : ""}`}>All ({leads.length})</button>
        {STATUSES.map((s) => (
          <button key={s} data-testid={`leads-filter-${s}`} onClick={() => setFilter(s)} className={`chip ${filter === s ? "active" : ""}`}>
            {s} ({leads.filter((l) => l.status === s).length})
          </button>
        ))}
      </div>
      {shown.length === 0 && <p className="text-sm text-[#666]">No leads here yet.</p>}
      <div className="space-y-3">
        {shown.map((l) => (
          <div key={l.id} data-testid={`lead-${l.id}`} className="border-2 border-[#111] p-4 md:p-5 bg-[#F2F2EF]">
            <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-display text-xl md:text-2xl uppercase tracking-tight">{l.name}</span>
                  <span className="chip text-[10px]">{l.service}</span>
                  {l.suburb && <span className="chip text-[10px] chip-olive">{l.suburb}</span>}
                </div>
                <div className="mt-2 flex flex-wrap gap-4 text-sm text-[#444]">
                  <a href={`tel:${l.phone}`} className="flex items-center gap-1 hover:text-[#D85C3A]"><PhoneIcon size={14} /> {l.phone}</a>
                  <a href={`mailto:${l.email}`} className="flex items-center gap-1 hover:text-[#D85C3A] break-all"><EnvelopeSimple size={14} /> {l.email}</a>
                </div>
                {l.message && <p className="mt-3 text-sm text-[#222]">{l.message}</p>}
                {l.image_base64 && <img src={l.image_base64} alt="lead" className="mt-3 max-h-40 border-2 border-[#111]" />}
                <div className="text-[10px] uppercase tracking-widest text-[#888] mt-3">
                  {new Date(l.created_at).toLocaleString()}
                </div>
              </div>
              <div className="flex flex-col gap-2 md:items-end">
                <select
                  data-testid={`lead-status-${l.id}`}
                  value={l.status}
                  onChange={(e) => updateStatus(l.id, e.target.value)}
                  className="border-2 border-[#111] bg-[#F2F2EF] px-2 py-1 text-sm uppercase tracking-widest"
                >
                  {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
                <button data-testid={`lead-delete-${l.id}`} onClick={() => del(l.id)} className="border-2 border-[#111] p-2 hover:bg-[#D85C3A] hover:text-white" aria-label="delete">
                  <Trash size={16} />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function ProjectsTab() {
  const [items, setItems] = useState([]);
  const [show, setShow] = useState(false);
  const [form, setForm] = useState({ title: "", category: "interior", suburb: "", description: "", image_url: "", featured: false });
  const load = useCallback(async () => { const r = await api.get("/projects"); setItems(r.data); }, []);
  useEffect(() => { load(); }, [load]);
  const create = async (e) => {
    e.preventDefault();
    await api.post("/projects", form, { headers: adminAuthHeaders() });
    setShow(false); setForm({ title: "", category: "interior", suburb: "", description: "", image_url: "", featured: false }); load();
  };
  const del = async (id) => {
    if (!confirm("Delete?")) return;
    await api.delete(`/projects/${id}`, { headers: adminAuthHeaders() });
    load();
  };
  return (
    <div>
      <button data-testid="add-project" onClick={() => setShow(true)} className="btn-brut mb-5"><Plus size={18} weight="bold" /> Add project</button>
      {show && (
        <form onSubmit={create} className="border-2 border-[#111] p-5 mb-5 bg-[#F2F2EF] grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="project-form">
          <input className="input-brut" placeholder="Title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required />
          <select className="input-brut" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>
            {["residential", "interior", "exterior", "commercial"].map((c) => <option key={c}>{c}</option>)}
          </select>
          <input className="input-brut" placeholder="Suburb" value={form.suburb} onChange={(e) => setForm({ ...form, suburb: e.target.value })} />
          <input className="input-brut" placeholder="Image URL" value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} required />
          <textarea className="input-brut md:col-span-2" placeholder="Description" rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} required />
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.featured} onChange={(e) => setForm({ ...form, featured: e.target.checked })} /> Featured</label>
          <div className="md:col-span-2 flex gap-3">
            <button type="submit" className="btn-brut">Save</button>
            <button type="button" onClick={() => setShow(false)} className="btn-outline-brut">Cancel</button>
          </div>
        </form>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map((p) => (
          <div key={p.id} className="border-2 border-[#111] bg-[#F2F2EF]">
            <div className="aspect-[16/10] overflow-hidden border-b-2 border-[#111]"><img src={p.image_url} alt={p.title} className="w-full h-full object-cover" /></div>
            <div className="p-4">
              <div className="flex items-start justify-between">
                <div>
                  <span className="chip text-[10px]">{p.category}</span>
                  <h4 className="font-display text-lg mt-2 uppercase tracking-tight">{p.title}</h4>
                  <div className="text-xs text-[#666]">{p.suburb}</div>
                </div>
                <button onClick={() => del(p.id)} className="border-2 border-[#111] p-2 hover:bg-[#D85C3A] hover:text-white" data-testid={`project-delete-${p.id}`}><Trash size={14} /></button>
              </div>
              <p className="text-sm text-[#444] mt-2 line-clamp-3">{p.description}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function TestimonialsTab() {
  const [items, setItems] = useState([]);
  const [show, setShow] = useState(false);
  const [form, setForm] = useState({ name: "", suburb: "", rating: 5, text: "" });
  const load = useCallback(async () => { const r = await api.get("/testimonials"); setItems(r.data); }, []);
  useEffect(() => { load(); }, [load]);
  const create = async (e) => {
    e.preventDefault();
    await api.post("/testimonials", form, { headers: adminAuthHeaders() });
    setShow(false); setForm({ name: "", suburb: "", rating: 5, text: "" }); load();
  };
  const del = async (id) => {
    if (!confirm("Delete?")) return;
    await api.delete(`/testimonials/${id}`, { headers: adminAuthHeaders() }); load();
  };
  return (
    <div>
      <button data-testid="add-testimonial" onClick={() => setShow(true)} className="btn-brut mb-5"><Plus size={18} weight="bold" /> Add testimonial</button>
      {show && (
        <form onSubmit={create} className="border-2 border-[#111] p-5 mb-5 bg-[#F2F2EF] grid grid-cols-1 md:grid-cols-2 gap-3">
          <input className="input-brut" placeholder="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
          <input className="input-brut" placeholder="Suburb" value={form.suburb} onChange={(e) => setForm({ ...form, suburb: e.target.value })} />
          <input className="input-brut" type="number" min={1} max={5} value={form.rating} onChange={(e) => setForm({ ...form, rating: parseInt(e.target.value) })} />
          <textarea className="input-brut md:col-span-2" rows={3} placeholder="Quote" value={form.text} onChange={(e) => setForm({ ...form, text: e.target.value })} required />
          <div className="md:col-span-2 flex gap-3">
            <button type="submit" className="btn-brut">Save</button>
            <button type="button" onClick={() => setShow(false)} className="btn-outline-brut">Cancel</button>
          </div>
        </form>
      )}
      <div className="space-y-3">
        {items.map((t) => (
          <div key={t.id} className="border-2 border-[#111] p-4 bg-[#F2F2EF] flex justify-between gap-3">
            <div>
              <div className="font-bold">{t.name} <span className="text-[#666] text-sm font-normal">· {t.suburb}</span></div>
              <p className="text-sm mt-1">"{t.text}"</p>
            </div>
            <button onClick={() => del(t.id)} className="border-2 border-[#111] p-2 self-start hover:bg-[#D85C3A] hover:text-white"><Trash size={14} /></button>
          </div>
        ))}
      </div>
    </div>
  );
}

function BlogTab() {
  const [items, setItems] = useState([]);
  const [show, setShow] = useState(false);
  const [form, setForm] = useState({ slug: "", title: "", excerpt: "", content: "", cover_image: "", tags: "", published: true });
  const load = useCallback(async () => { const r = await api.get("/blog"); setItems(r.data); }, []);
  useEffect(() => { load(); }, [load]);
  const create = async (e) => {
    e.preventDefault();
    const payload = { ...form, tags: form.tags.split(",").map((t) => t.trim()).filter(Boolean) };
    await api.post("/blog", payload, { headers: adminAuthHeaders() });
    setShow(false); setForm({ slug: "", title: "", excerpt: "", content: "", cover_image: "", tags: "", published: true }); load();
  };
  const del = async (id) => {
    if (!confirm("Delete?")) return;
    await api.delete(`/blog/${id}`, { headers: adminAuthHeaders() }); load();
  };
  return (
    <div>
      <button data-testid="add-blog" onClick={() => setShow(true)} className="btn-brut mb-5"><Plus size={18} weight="bold" /> Add post</button>
      {show && (
        <form onSubmit={create} className="border-2 border-[#111] p-5 mb-5 bg-[#F2F2EF] grid grid-cols-1 md:grid-cols-2 gap-3">
          <input className="input-brut" placeholder="Slug" value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} required />
          <input className="input-brut" placeholder="Title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required />
          <input className="input-brut" placeholder="Cover image URL" value={form.cover_image} onChange={(e) => setForm({ ...form, cover_image: e.target.value })} />
          <input className="input-brut" placeholder="Tags (comma separated)" value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} />
          <textarea className="input-brut md:col-span-2" rows={2} placeholder="Excerpt" value={form.excerpt} onChange={(e) => setForm({ ...form, excerpt: e.target.value })} required />
          <textarea className="input-brut md:col-span-2" rows={6} placeholder="Content" value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} required />
          <div className="md:col-span-2 flex gap-3">
            <button type="submit" className="btn-brut">Save</button>
            <button type="button" onClick={() => setShow(false)} className="btn-outline-brut">Cancel</button>
          </div>
        </form>
      )}
      <div className="space-y-3">
        {items.map((p) => (
          <div key={p.id} className="border-2 border-[#111] p-4 bg-[#F2F2EF] flex justify-between gap-3">
            <div className="min-w-0">
              <div className="font-display text-xl uppercase tracking-tight truncate">{p.title}</div>
              <div className="text-xs text-[#666]">{p.slug}</div>
              <p className="text-sm mt-2 line-clamp-2">{p.excerpt}</p>
            </div>
            <button onClick={() => del(p.id)} className="border-2 border-[#111] p-2 self-start hover:bg-[#D85C3A] hover:text-white"><Trash size={14} /></button>
          </div>
        ))}
      </div>
    </div>
  );
}

function SettingsTab() {
  const [s, setS] = useState(null);
  const [apiKey, setApiKey] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [msg, setMsg] = useState("");
  const [testTo, setTestTo] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const r = await api.get("/admin/settings", { headers: adminAuthHeaders() });
    setS(r.data);
    setFrom(r.data.resend_from || "");
    setTo(r.data.resend_to || "");
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async (e) => {
    e.preventDefault();
    setBusy(true); setMsg("");
    try {
      const payload = { resend_from: from, resend_to: to };
      if (apiKey.trim()) payload.resend_api_key = apiKey.trim();
      await api.patch("/admin/settings", payload, { headers: adminAuthHeaders() });
      setApiKey("");
      setMsg("✓ Saved.");
      await load();
    } catch (err) {
      setMsg(`✗ ${err?.response?.data?.detail || err.message}`);
    } finally { setBusy(false); }
  };

  const sendTest = async () => {
    setBusy(true); setMsg("");
    try {
      const r = await api.post("/admin/test-email", { to: testTo || null }, { headers: adminAuthHeaders() });
      setMsg(`✓ ${r.data.detail}`);
    } catch (err) {
      setMsg(`✗ ${err?.response?.data?.detail || err.message}`);
    } finally { setBusy(false); }
  };

  const remove = async () => {
    if (!confirm("Remove the saved Resend API key?")) return;
    setBusy(true); setMsg("");
    try {
      await api.patch("/admin/settings", { resend_api_key: "REMOVE" }, { headers: adminAuthHeaders() });
      setMsg("✓ Key removed.");
      await load();
    } finally { setBusy(false); }
  };

  if (!s) return <p className="text-sm text-[#666]">Loading…</p>;

  return (
    <div className="max-w-2xl">
      <div className="border-2 border-[#111] p-5 bg-[#F2F2EF] mb-6">
        <div className="flex items-center gap-2 mb-2">
          <span className={`w-3 h-3 ${s.resend_configured ? "bg-[#6A7B61]" : "bg-[#D85C3A]"}`} />
          <span className="font-bold uppercase tracking-widest text-xs">
            Email notifications: {s.resend_configured ? `ACTIVE (${s.resend_key_source})` : "NOT CONFIGURED"}
          </span>
        </div>
        <p className="text-sm text-[#666]">
          When a new lead is submitted, an email is sent to <strong>{s.resend_to || "—"}</strong>.
          {!s.resend_configured && " Paste your Resend API key below to switch on. Get one at "}
          {!s.resend_configured && <a href="https://resend.com/api-keys" target="_blank" rel="noreferrer" className="text-[#D85C3A] underline">resend.com/api-keys</a>}
          {!s.resend_configured && "."}
        </p>
      </div>

      <form onSubmit={save} data-testid="settings-form" className="border-2 border-[#111] p-5 md:p-6 bg-[#F2F2EF] space-y-4">
        <div>
          <label className="text-xs uppercase tracking-widest text-[#666]">Resend API key</label>
          <input
            data-testid="settings-resend-key"
            type="password"
            className="input-brut mt-1"
            placeholder={s.resend_configured ? "•••••••• (saved — paste new to overwrite)" : "re_..."}
            value={apiKey}
            onChange={(e) => setApiKey(e.target.value)}
            autoComplete="off"
          />
          <p className="text-[10px] text-[#666] mt-1">The key is encrypted in storage. Use the same Resend account that has themelbournepainters.com.au verified.</p>
        </div>
        <div>
          <label className="text-xs uppercase tracking-widest text-[#666]">From (must be verified domain)</label>
          <input data-testid="settings-from" className="input-brut mt-1" value={from} onChange={(e) => setFrom(e.target.value)} placeholder="The Melbourne Painters <noreply@themelbournepainters.com.au>" />
        </div>
        <div>
          <label className="text-xs uppercase tracking-widest text-[#666]">Notifications to (your email)</label>
          <input data-testid="settings-to" className="input-brut mt-1" value={to} onChange={(e) => setTo(e.target.value)} placeholder="Gurdeep@themelbournepainters.com.au" />
        </div>
        <div className="flex flex-wrap gap-3 pt-2">
          <button type="submit" data-testid="settings-save" disabled={busy} className="btn-brut">Save settings</button>
          {s.resend_configured && (
            <button type="button" onClick={remove} disabled={busy} className="btn-outline-brut">Remove key</button>
          )}
        </div>
      </form>

      <div className="mt-6 border-2 border-[#111] p-5 md:p-6 bg-[#F2F2EF]">
        <h4 className="font-display text-xl uppercase tracking-tight mb-3">Send test email</h4>
        <p className="text-sm text-[#666] mb-3">Confirm your key + domain work end-to-end.</p>
        <div className="flex flex-col sm:flex-row gap-3">
          <input data-testid="settings-test-to" className="input-brut flex-1" placeholder={`Leave blank to send to ${s.resend_to || "yourself"}`} value={testTo} onChange={(e) => setTestTo(e.target.value)} />
          <button data-testid="settings-test-send" onClick={sendTest} disabled={busy || !s.resend_configured} className="btn-brut">Send test</button>
        </div>
        {!s.resend_configured && <p className="text-xs text-[#B94A2E] mt-2">Save an API key first.</p>}
      </div>

      {msg && <p data-testid="settings-msg" className={`mt-4 text-sm font-bold ${msg.startsWith("✓") ? "text-[#6A7B61]" : "text-[#B94A2E]"}`}>{msg}</p>}
    </div>
  );
}

function ContentTab() {
  const [queue, setQueue] = useState(null);
  const [busy, setBusy] = useState(false);
  const [last, setLast] = useState(null);
  const [msg, setMsg] = useState("");

  const load = useCallback(async () => {
    const r = await api.get("/admin/content/queue", { headers: adminAuthHeaders() });
    setQueue(r.data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const generate = async () => {
    setBusy(true); setMsg(""); setLast(null);
    try {
      const r = await api.post("/admin/content/generate", {}, { headers: adminAuthHeaders(), timeout: 90000 });
      setLast(r.data);
      setMsg("✓ Published.");
      await load();
    } catch (err) {
      setMsg(`✗ ${err?.response?.data?.detail || err.message}`);
    } finally { setBusy(false); }
  };

  if (!queue) return <p className="text-sm text-[#666]">Loading…</p>;

  return (
    <div className="max-w-3xl">
      <div className="grid grid-cols-3 gap-3 mb-6">
        <Stat label="Posts generated" value={queue.used_count} />
        <Stat label="In queue" value={queue.remaining} />
        <Stat label="Total combos" value={queue.total_combos} />
      </div>

      <div className="border-2 border-[#111] p-5 md:p-6 bg-[#F2F2EF] mb-6">
        <h4 className="font-display text-xl uppercase tracking-tight mb-1">Generate next blog post</h4>
        <p className="text-sm text-[#666] mb-4">
          Uses Claude Sonnet 4.5 to write a 600-900 word SEO-optimised post targeting long-tail Melbourne painting keywords.
          Each post auto-cross-links to the relevant suburb page. Recommended cadence: 2/week.
        </p>
        <button data-testid="content-generate" onClick={generate} disabled={busy || queue.remaining === 0} className="btn-brut">
          {busy ? "Writing… (10-20s)" : queue.remaining === 0 ? "Queue exhausted" : "Generate + publish 1 post"}
        </button>
        {msg && <p data-testid="content-msg" className={`mt-3 text-sm font-bold ${msg.startsWith("✓") ? "text-[#6A7B61]" : "text-[#B94A2E]"}`}>{msg}</p>}
        {last && (
          <div className="mt-4 p-4 border-2 border-[#111] bg-[#EAEAE5]">
            <div className="text-xs uppercase tracking-widest text-[#666] mb-1">Just published</div>
            <div className="font-display text-lg leading-tight">{last.title}</div>
            <div className="text-xs text-[#666] mt-1">slug: {last.slug} · {last.tags?.join(" · ")}</div>
          </div>
        )}
      </div>

      <div className="border-2 border-[#111] p-5 md:p-6 bg-[#F2F2EF]">
        <h4 className="font-display text-xl uppercase tracking-tight mb-3">Upcoming queue (next 10)</h4>
        <ol className="space-y-2">
          {queue.upcoming.map((u) => (
            <li key={u.combo} className="flex items-start gap-3 border-l-4 border-[#D85C3A] pl-3 py-1">
              <span className="text-[10px] uppercase tracking-widest text-[#999] mt-1 flex-shrink-0">{u.suburb}</span>
              <span className="text-sm">{u.title_preview}</span>
            </li>
          ))}
        </ol>
      </div>
    </div>
  );
}

export default function Admin() {
  const [authed, setAuthed] = useState(!!adminToken());
  const [tab, setTab] = useState("leads");
  const [stats, setStats] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!authed) return;
    api.get("/admin/stats", { headers: adminAuthHeaders() })
      .then((r) => setStats(r.data))
      .catch(() => { clearAdminToken(); setAuthed(false); });
  }, [authed, tab]);

  if (!authed) {
    return (
      <>
        <Seo title="Admin Login · The Melbourne Painters" description="Admin login" path="/admin" noindex />
        <Login onLogin={() => setAuthed(true)} />
      </>
    );
  }

  const logout = () => { clearAdminToken(); setAuthed(false); navigate("/"); };

  return (
    <div className="min-h-screen bg-[#F2F2EF]">
      <Seo title="Admin · The Melbourne Painters" description="Admin dashboard" path="/admin" noindex />
      <header className="border-b-2 border-[#111] bg-[#1A1A1A] text-[#F2F2EF] px-5 md:px-10 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Link to="/" data-testid="admin-back" className="text-xs uppercase tracking-widest text-[#999] hover:text-[#D85C3A] flex items-center gap-1">
            <ArrowLeft size={14} /> Site
          </Link>
          <div className="font-display text-xl md:text-2xl uppercase tracking-tight">TMP · Admin</div>
        </div>
        <button data-testid="admin-logout" onClick={logout} className="text-xs uppercase tracking-widest text-[#999] hover:text-[#D85C3A]">Logout</button>
      </header>

      <main className="px-5 md:px-10 py-8">
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-8">
            <Stat label="Total leads" value={stats.total_leads} />
            <Stat label="New leads" value={stats.new_leads} />
            <Stat label="Won" value={stats.won_leads} />
            <Stat label="Projects" value={stats.projects} />
            <Stat label="Blog posts" value={stats.blog_posts} />
          </div>
        )}

        <div className="flex flex-wrap gap-2 mb-6">
          {[
            ["leads", "Leads"],
            ["projects", "Projects"],
            ["testimonials", "Testimonials"],
            ["blog", "Blog"],
            ["content", "Content Engine"],
            ["settings", "Settings"],
          ].map(([k, label]) => (
            <button
              key={k}
              data-testid={`admin-tab-${k}`}
              onClick={() => setTab(k)}
              className={`chip ${tab === k ? "active" : ""}`}
            >
              {label}
            </button>
          ))}
        </div>

        {tab === "leads" && <LeadsTab />}
        {tab === "projects" && <ProjectsTab />}
        {tab === "testimonials" && <TestimonialsTab />}
        {tab === "blog" && <BlogTab />}
        {tab === "content" && <ContentTab />}
        {tab === "settings" && <SettingsTab />}
      </main>
    </div>
  );
}
